<template>
  <div
    ref="containerEl"
    class="search-types"
  >
    <div class="search-input">
      <TextInput
        :value="searchInput"
        :loading="isLoading"
        icon-before="fas fa-search"
        placeholder="Search for document types"
        type="search"
        @focus="isFocused = true"
        @input="handleSearch"
      />

      <div
        class="stk-dropdown-box search-results"
        :class="{ 'is-active': isFocused}"
      >
        <div
          v-if="isLoading"
          class="search-info"
        >
          <LoaderWithText />
        </div>

        <template v-else-if="searchInput && hasFetched">
          <Stack
            v-if="data.document_types?.length"
            class="document-list"
            :class="{
              'is-faded': isLoading,
            }"
            gap="1"
          >
            <MenuItem
              v-for="type in data.document_types"
              :key="type.id"
              class="document-item"
              :selected="alreadyPresentIds.includes(type.id)"
              :static="alreadyPresentIds.includes(type.id)"
              @click="handleItemSelection(type)"
            >
              {{ type.name }}
            </MenuItem>
          </Stack>

          <Stack
            v-else
            class="search-info"
            :class="{
              'is-faded': isLoading,
            }"
            gap="2"
          >
            <div>
              No result for <strong>{{ searchInput }}</strong>
            </div>

            <div class="not-exist">
              <p>This document type does not exist but you can add it manually.</p>
              <Button
                variant="primary"
                icon-after="fas fa-plus"
                @click="handleNewAddition"
              >
                Add "{{ searchInput }}" as a Custom Document
              </Button>
            </div>
          </Stack>
        </template>

        <div
          v-else
          class="search-info"
          :class="{
            'is-faded': isLoading,
          }"
        >
          Start typing to search for document types to add to this section
        </div>
      </div>
    </div>

    <Button
      icon-before="fas fa-eye"
      @click="showModal = true"
    >
      Browse all
    </Button>

    <StakeModal
      v-model="showModal"
      no-padding
    >
      <DocumentTypesBrowser
        :already-present="alreadyPresent"
        @close="showModal = false"
        @addition="documentType => emit('addition', documentType)"
      />
    </StakeModal>
  </div>
</template>

<script setup>
import { computed, defineEmits, onMounted, onUnmounted, ref } from 'vue';
import { Button, LoaderWithText, MenuItem, Stack, StakeModal, TextInput } from '@stakeui';
import DocumentTypesBrowser from './DocumentTypesBrowser.vue';

const props = defineProps({
  alreadyPresent: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['addition']);
const containerEl = ref(null);
const showModal = ref(false);
const data = ref({});
const searchInput = ref('');
const isFocused = ref(false);
const isLoading = ref(false);
const hasFetched = ref(false);

let controller;
let debounceId;

// Computed
const alreadyPresentIds = computed(() => props.alreadyPresent.map(el => el.document_type_id));

// Async
const performSearch = async query => {
  // Guard against stale timers
  if (query !== searchInput.value) {
    return;
  }

  if (controller) {
    controller.abort();
  }

  if (!searchInput.value) {
    data.value = {};
    isLoading.value = false;
    return;
  }

  controller = new AbortController();

  try {
    const params = new URLSearchParams({
      search: query,
    });
    const url = `/api/v1/client-portal/document-types/?${params.toString()}`;

    const res = await fetch(url, {
      signal: controller.signal,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!res.ok) {
      throw new Error(`HTTP error: ${res.status}`);
    }

    data.value = await res.json();
  } catch (err) {
    if (err.name === 'AbortError') {
      console.log('Request aborted');
    } else {
      console.error('Error in fetching documnet types:', err);
    }
  } finally {
    isLoading.value = false;
    hasFetched.value = true;
  }
};

// Handlers
const handleClickOutside = event => {
  if (containerEl.value && !containerEl.value.contains(event.target)) {
    isFocused.value = false;
  }
};

const handleEscape = event => {
  if (event.key === 'Escape') {
    isFocused.value = false;
  }
};

const handleSearch = val => {
  const next = (val ?? '').trim();
  searchInput.value = next;

  // Full reset when empty
  if (!next) {
    clearTimeout(debounceId);
    if (controller) controller.abort();
    isLoading.value = false;
    return;
  }

  clearTimeout(debounceId);
  isLoading.value = true;

  debounceId = setTimeout(() => performSearch(next), 200);
};

const handleItemSelection = documentType => {
  emit('addition', documentType);
};

const handleNewAddition = () => {
  emit('new', searchInput.value);
  searchInput.value = '';
};

// Lifecycle
onMounted(() => {
  document.addEventListener('click', handleClickOutside);
  window.addEventListener('keydown', handleEscape);
});

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside);
  window.removeEventListener('keydown', handleEscape);
  clearTimeout(debounceId);
  if (controller) controller.abort();
});
</script>

<style scoped>
.search-types {
  display: flex;
  align-items: center;
  gap: 0.5em;
  font-size: 0.875em;
}

.search-info {
  padding: 0.375em 0.625em;
}

.not-exist {
  background-color: var(--stk-primary-90);
  color: var(--stk-primary-40);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  border-radius: 0.75em;
  padding: 1.5em 1em;
  gap: 1em;
}

.search-input {
  flex-grow: 1;
  display: flex;
  position: relative;
}

.search-results {
  position: absolute;
  top: calc(100% + 2px);
  left: 0;
  right: 0;
  z-index: 3;
  width: auto;
  overflow-x: hidden;
  overflow-y: scroll;
  height: 16em;

  &.is-active {
      opacity: 1;
      pointer-events: auto;
      transform: none;
  }
}

.is-faded {
  opacity: 0.5;
}
</style>
